/**
 * Zone2Conformite — Enquêtes de crédit + RL-31 + Renouvellements/lettres TAL + Archivage Drive.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FileSignature, Shield, Loader2, ExternalLink, Download,
  FileText, Send, UploadCloud, Plus, Pencil,
} from "lucide-react";
import { toast } from "@/lib/toast";
import {
  API, authH, dollars, archiveImmeubleItem,
  TerminerButton, ZoneHeader, StatusBadge, PaymentBadge,
} from "./_shared";
import {
  CreateCreditCheckModal, CreateRl31Modal, CreateRentIncreaseModal,
} from "./Zone2CreateModals";

export default function Zone2Conformite({ masterId }) {
  const [rl31, setRl31] = useState([]);
  const [rentIncreases, setRentIncreases] = useState([]);
  const [creditChecks, setCreditChecks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCC, setLoadingCC] = useState(false);
  const [archiveBusy, setArchiveBusy] = useState(null);
  const [selectedCC, setSelectedCC] = useState(null);
  // Batch #14.214 — Modals création admin (au nom du client)
  const [showCreateCC, setShowCreateCC] = useState(false);
  const [showCreateRl31, setShowCreateRl31] = useState(false);
  const [showCreateRI, setShowCreateRI] = useState(false);
  // Édition admin (réutilise les modals avec `existing` prop)
  const [editingCC, setEditingCC] = useState(null);
  const [editingRl31, setEditingRl31] = useState(null);
  const [editingRI, setEditingRI] = useState(null);
  // Données auxiliaires pour les sélecteurs (units + properties)
  const [overview, setOverview] = useState(null);

  const loadOverview = useCallback(async () => {
    if (!masterId) return;
    try {
      const r = await axios.get(`${API}/immeuble/overview-global?master_id=${encodeURIComponent(masterId)}`, { headers: authH() });
      setOverview(r.data);
    } catch { /* silent */ }
  }, [masterId]);

  const unitsForSelectors = useMemo(() => {
    if (!overview) return [];
    const buildings = overview.buildings || [];
    const buildingsById = Object.fromEntries(buildings.map((b) => [b.id, b]));
    return (overview.units || []).map((u) => ({
      ...u,
      _building: buildingsById[u.property_id] || null,
      _lease: (overview.leases || []).find((L) => L.unit_id === u.id && L.status === "active") || null,
    }));
  }, [overview]);
  const propertiesForSelectors = useMemo(() => overview?.buildings || [], [overview]);

  const loadCC = useCallback(async () => {
    if (!masterId) return;
    setLoadingCC(true);
    try {
      const r = await axios.get(`${API}/admin/immeuble/${masterId}/zone1/credit-checks`, { headers: authH() });
      setCreditChecks(r.data?.items || []);
    } catch { /* silent */ }
    finally { setLoadingCC(false); }
  }, [masterId]);

  const load = useCallback(async () => {
    if (!masterId) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/admin/immeuble/${masterId}/zone2/rl31-requests`, { headers: authH() }),
        axios.get(`${API}/admin/immeuble/${masterId}/zone2/rent-increase-requests`, { headers: authH() }),
      ]);
      setRl31(a.data?.items || []);
      setRentIncreases(b.data?.items || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [masterId]);

  useEffect(() => { load(); loadCC(); loadOverview(); }, [load, loadCC, loadOverview]);

  const terminerCC = async (cc) => {
    if (!window.confirm("Terminer cette enquête de crédit ?\n\n• Elle disparaîtra de cette liste\n• Visible 15 jours dans Archives puis supprimée\n\nConfirmer ?")) return;
    setArchiveBusy(cc.id);
    try {
      await archiveImmeubleItem({
        masterId, entityType: "credit_check", entityId: cc.id,
        label: `Enquête ${cc.candidate_first_name || ""} ${cc.candidate_last_name || ""}`.trim() || "Enquête de crédit",
        snapshot: cc,
      });
      toast.success("Enquête archivée");
      loadCC();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setArchiveBusy(null); }
  };

  const terminerRl31 = async (r) => {
    if (!window.confirm("Terminer cette demande RL-31 ?\n\n• Elle disparaîtra de cette liste\n• Visible 15 jours dans Archives puis supprimée\n\nConfirmer ?")) return;
    setArchiveBusy(r.id);
    try {
      await archiveImmeubleItem({
        masterId, entityType: "rl31", entityId: r.id,
        label: r.title || "RL-31",
        snapshot: r,
      });
      toast.success("Demande RL-31 archivée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setArchiveBusy(null); }
  };

  const terminerRentIncrease = async (ri) => {
    if (!window.confirm("Terminer cette hausse de loyer ?\n\n• Elle disparaîtra de cette liste\n• Visible 15 jours dans Archives puis supprimée\n\nConfirmer ?")) return;
    setArchiveBusy(ri.id);
    try {
      await archiveImmeubleItem({
        masterId, entityType: "rent_increase", entityId: ri.id,
        label: `Hausse ${ri.property_address || ""}${ri.unit_label ? ` · apt ${ri.unit_label}` : ""}`.trim() || "Hausse de loyer",
        snapshot: ri,
      });
      toast.success("Hausse archivée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setArchiveBusy(null); }
  };

  const generateLetter = async (reqId) => {
    try {
      const r = await axios.post(`${API}/admin/immeuble/zone2/rent-increase-requests/${reqId}/generate-letter`, {}, {
        headers: authH(), responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      toast.success("Lettre générée et archivée dans Drive");
      load();
    } catch (e) {
      let msg = "Erreur génération";
      if (e.response?.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          const json = JSON.parse(txt);
          msg = json.detail || msg;
        } catch { /* ignore */ }
      } else {
        msg = e.response?.data?.detail || msg;
      }
      toast.error(msg);
    }
  };

  const sendForSignature = async (ri) => {
    const reqId = typeof ri === "string" ? ri : ri.id;
    const hasTenant2 = typeof ri === "object" && !!ri.tenant_2_name;
    const existingEmail = typeof ri === "object" ? (ri.tenant_email || "") : "";
    const existingEmail2 = typeof ri === "object" ? (ri.tenant_2_email || "") : "";
    if (!window.confirm(
      `Envoyer l'avis ${hasTenant2 ? "aux 2 locataires (en parallèle)" : "au locataire"} pour signature ?\n\n` +
      `• ${hasTenant2 ? "Chacun reçoit son lien magique" : "Le locataire recevra un courriel avec un lien magique"}\n` +
      "• Il(s) pourra(ont) accepter ou refuser, puis signer\n" +
      `• Une fois ${hasTenant2 ? "les 2 signatures obtenues" : "signé"}, le propriétaire recevra le lien pour accuser réception\n` +
      "• PDF final archivé sur Drive + copie envoyée par courriel au propriétaire et au cabinet"
    )) return;

    let tenant_email = existingEmail;
    if (!tenant_email) {
      tenant_email = window.prompt("Adresse courriel du locataire #1 (obligatoire) :", "");
      if (!tenant_email) {
        toast.error("Email requis");
        return;
      }
      tenant_email = tenant_email.trim();
    }
    let tenant_2_email = existingEmail2;
    if (hasTenant2 && !tenant_2_email) {
      tenant_2_email = window.prompt(`Adresse courriel du locataire #2 (${ri.tenant_2_name}) :`, "");
      if (!tenant_2_email) {
        toast.error("Email du 2e locataire requis");
        return;
      }
      tenant_2_email = tenant_2_email.trim();
    }
    try {
      const body = { tenant_email, origin_url: window.location.origin };
      if (hasTenant2) body.tenant_2_email = tenant_2_email;
      const r = await axios.post(
        `${API}/admin/zone2/rent-increase-requests/${reqId}/send-for-signature`,
        body, { headers: authH() }
      );
      toast.success(r.data?.message || "Liens de signature envoyés");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    }
  };

  const pushToDrive = async (ri) => {
    if (!window.confirm(`Pousser la lettre signée au Drive de l'immeuble ?\n\nUtile si l'archivage automatique a échoué.`)) return;
    try {
      const r = await axios.post(
        `${API}/admin/immeuble/zone2/rent-increase-requests/${ri.id}/push-to-drive`,
        {}, { headers: authH() }
      );
      toast.success("Lettre déposée sur Drive");
      if (r.data?.drive_link) {
        window.open(r.data.drive_link, "_blank");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur upload Drive");
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="admin-immeuble-zone-2">
      <ZoneHeader index={2} title="Demandes en attente" icon={Shield} tone="amber"
        subtitle="Enquêtes de crédit · RL-31 · Renouvellements / lettres TAL · Archivage Drive automatique" />

      <div className="p-5 space-y-5">
        {/* Bloc Enquêtes de crédit (déplacé depuis Zone1 — Batch #14.213) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider inline-flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-emerald-600" /> Enquêtes de crédit
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400">{creditChecks.length} demande(s)</span>
              <button
                onClick={() => setShowCreateCC(true)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold inline-flex items-center gap-1"
                data-testid="zone2-create-cc-btn"
              >
                <Plus className="w-3 h-3" /> Nouvelle enquête
              </button>
            </div>
          </div>
          {loadingCC ? (
            <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400 inline" /></div>
          ) : creditChecks.length === 0 ? (
            <p className="text-xs text-slate-400 italic px-2 py-3">Aucune enquête créée par le client.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="zone2-credit-checks-list">
              {creditChecks.map((cc) => (
                <li key={cc.id} className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 text-xs hover:bg-emerald-50 cursor-pointer transition" onClick={() => setSelectedCC(cc)} data-testid={`zone2-cc-row-${cc.id}`}>
                  <div className="flex items-center gap-3">
                    <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {cc.candidate_first_name} {cc.candidate_last_name}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>Logement {cc.unit_label || cc.unit_id?.slice(0, 6)}</span>
                        <span>·</span>
                        <span>Créée {(cc.created_at || "").slice(0, 10)}</span>
                        <PaymentBadge status={cc.payment_status} amount={cc.payment_amount_cad} />
                      </div>
                    </div>
                    <select
                      value={cc.status || "pending"}
                      onClick={(e) => e.stopPropagation()}
                      onChange={async (e) => {
                        e.stopPropagation();
                        try {
                          await axios.put(`${API}/admin/immeuble/credit-checks/${cc.id}/status`,
                            { status: e.target.value }, { headers: authH() });
                          toast.success("Statut mis à jour");
                          loadCC();
                        } catch (er) {
                          toast.error(er.response?.data?.detail || "Erreur");
                        }
                      }}
                      className="text-[10px] border rounded px-1.5 py-0.5 bg-white"
                      data-testid={`zone2-cc-status-${cc.id}`}
                    >
                      <option value="pending">En attente</option>
                      <option value="in_progress">En cours</option>
                      <option value="accepted">Accepté</option>
                      <option value="rejected">Refusé</option>
                      <option value="completed">Complété</option>
                    </select>
                    <span onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setEditingCC(cc)}
                        className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[10px] inline-flex items-center gap-1 mr-1"
                        title="Modifier en détail"
                        data-testid={`zone2-cc-edit-${cc.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Modifier
                      </button>
                      <TerminerButton onClick={() => terminerCC(cc)} busy={archiveBusy === cc.id} testId={`zone2-cc-terminer-${cc.id}`} />
                    </span>
                  </div>
                  {cc.admin_notes && (
                    <div className="mt-2 ml-7 text-[10px] text-slate-500 italic">📝 {cc.admin_notes}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* RL-31 */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider inline-flex items-center gap-2">
              <FileText className="w-3.5 h-3.5 text-amber-600" /> RL-31 (présences au 31 décembre)
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400">{rl31.length} demande(s)</span>
              <button
                onClick={() => setShowCreateRl31(true)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold inline-flex items-center gap-1"
                data-testid="zone2-create-rl31-btn"
              >
                <Plus className="w-3 h-3" /> Nouvelle RL-31
              </button>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin text-slate-400 inline" /></div>
          ) : rl31.length === 0 ? (
            <p className="text-xs text-slate-400 italic px-2 py-3">Aucune saisie RL-31 reçue.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="zone2-rl31-list">
              {rl31.map((r) => (
                <li key={r.id} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs flex items-center gap-3">
                  <FileText className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{r.title || "RL-31"}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{(r.message || "").slice(0, 80)}</div>
                    {r.rl31_transmission && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">
                        ✓ Transmis le {(r.rl31_transmission.transmitted_at || "").slice(0, 10)}
                        {r.rl31_transmission.confirmation_number ? ` · Conf #${r.rl31_transmission.confirmation_number}` : ""}
                      </div>
                    )}
                  </div>
                  {r.status !== "transmitted" && (
                    <button
                      onClick={() => setEditingRl31(r)}
                      className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[10px] inline-flex items-center gap-1"
                      title="Modifier la demande"
                      data-testid={`zone2-rl31-edit-${r.id}`}
                    >
                      <Pencil className="w-3 h-3" /> Modifier
                    </button>
                  )}
                  {r.status !== "transmitted" && (
                    <button
                      onClick={async () => {
                        const conf = window.prompt("Numéro de confirmation Revenu Québec (optionnel) :", "");
                        if (conf === null) return;
                        try {
                          await axios.post(`${API}/admin/immeuble/rl31/${r.id}/transmit-government`,
                            { confirmation_number: conf || "" }, { headers: authH() });
                          toast.success("RL-31 marqué transmis au gouvernement");
                          load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
                      }}
                      className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-semibold inline-flex items-center gap-1"
                      data-testid={`zone2-rl31-transmit-${r.id}`}
                    >
                      <Send className="w-3 h-3" /> Transmettre au gouvernement
                    </button>
                  )}
                  <TerminerButton onClick={() => terminerRl31(r)} busy={archiveBusy === r.id} testId={`zone2-rl31-terminer-${r.id}`} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Renouvellements */}
        <div className="pt-3 border-t border-slate-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider inline-flex items-center gap-2">
              <FileSignature className="w-3.5 h-3.5 text-indigo-600" /> Renouvellements (hausses de loyer)
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-slate-400">{rentIncreases.length} demande(s)</span>
              <button
                onClick={() => setShowCreateRI(true)}
                className="text-[11px] px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold inline-flex items-center gap-1"
                data-testid="zone2-create-ri-btn"
              >
                <Plus className="w-3 h-3" /> Nouvelle hausse
              </button>
            </div>
          </div>
          {rentIncreases.length === 0 ? (
            <p className="text-xs text-slate-400 italic px-2 py-3">Aucune hausse demandée par le proprio.</p>
          ) : (
            <ul className="space-y-1.5" data-testid="zone2-rent-increases-list">
              {rentIncreases.map((ri) => (
                <li key={ri.id} className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-800 truncate">
                        {ri.property_address}{ri.unit_label ? ` · apt ${ri.unit_label}` : ""} — {ri.tenant_name || "locataire"}
                      </div>
                      <div className="text-[10px] text-slate-500 mt-0.5">
                        {dollars(ri.current_rent)} → <b className="text-indigo-700">{dollars(ri.new_rent)}</b>
                        {ri.effective_date ? ` · effet ${ri.effective_date}` : ""}
                        <StatusBadge status={ri.status} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap justify-end">
                      {/* Batch #14.214 — Bouton Modifier (admin agit pour le client) */}
                      {ri.status === "pending" && (
                        <button
                          onClick={() => setEditingRI(ri)}
                          className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-[10px] inline-flex items-center gap-1"
                          title="Modifier la demande"
                          data-testid={`zone2-ri-edit-${ri.id}`}
                        >
                          <Pencil className="w-3 h-3" /> Modifier
                        </button>
                      )}
                      {ri.letter_drive_web_view_link && (
                        <a href={ri.letter_drive_web_view_link} target="_blank" rel="noopener noreferrer"
                           className="px-2 py-1 rounded bg-white border border-slate-300 hover:bg-slate-50 text-[10px]">
                          <ExternalLink className="w-3 h-3 inline" /> Drive
                        </a>
                      )}
                      <button
                        onClick={() => generateLetter(ri.id)}
                        className="px-2.5 py-1 rounded bg-white border border-indigo-300 hover:bg-indigo-50 text-indigo-700 text-[10px] font-semibold inline-flex items-center gap-1"
                        data-testid={`zone2-generate-letter-${ri.id}`}
                      >
                        <Download className="w-3 h-3" /> {ri.letter_generated_at ? "Aperçu" : "Aperçu lettre"}
                      </button>
                      {ri.status !== "signature_completed" && (
                        <button
                          onClick={() => sendForSignature(ri)}
                          disabled={
                            ri.status === "signature_pending_tenant" ||
                            ri.status === "signature_pending_tenants" ||
                            ri.status === "signature_tenant_accepte" ||
                            ri.status === "signature_tenant_refuse" ||
                            ri.status === "signature_tenants_signed"
                          }
                          className="px-2.5 py-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-semibold inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid={`zone2-send-signature-${ri.id}`}
                          title={
                            ri.status === "signature_pending_tenants" ? "En attente signatures locataires" :
                            ri.status === "signature_pending_tenant" ? "En attente signature locataire" :
                            (ri.status === "signature_tenant_accepte" || ri.status === "signature_tenant_refuse" || ri.status === "signature_tenants_signed") ? "Finalisation en cours…" :
                            "Envoyer au(x) locataire(s) pour signature"
                          }
                        >
                          <FileSignature className="w-3 h-3" />
                          {ri.status === "signature_pending_tenants" ? "Locataires à signer" :
                           ri.status === "signature_pending_tenant" ? "Locataire à signer" :
                           (ri.status === "signature_tenant_accepte" || ri.status === "signature_tenant_refuse" || ri.status === "signature_tenants_signed") ? "Finalisation…" :
                           "Envoyer pour signature"}
                        </button>
                      )}
                      {ri.status === "signature_completed" && (
                        <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold inline-flex items-center gap-1">
                          ✓ Signé
                        </span>
                      )}
                      {ri.status === "signature_completed" && !ri.letter_drive_web_view_link && (
                        <button
                          onClick={() => pushToDrive(ri)}
                          className="px-2.5 py-1 rounded bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-semibold inline-flex items-center gap-1"
                          title="L'archivage automatique Drive a échoué — déposer manuellement"
                          data-testid={`zone2-push-drive-${ri.id}`}
                        >
                          <UploadCloud className="w-3 h-3" /> Pousser au Drive
                        </button>
                      )}
                      <TerminerButton onClick={() => terminerRentIncrease(ri)} busy={archiveBusy === ri.id} testId={`zone2-ri-terminer-${ri.id}`} />
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Modal détail enquête de crédit (déplacé depuis Zone1 — Batch #14.213) */}
      {selectedCC && (
        <CreditCheckDetailModal
          cc={selectedCC}
          onClose={() => setSelectedCC(null)}
          onReload={loadCC}
        />
      )}

      {/* Batch #14.214 — Modals création admin (au nom du client) */}
      {showCreateCC && (
        <CreateCreditCheckModal
          masterId={masterId}
          units={unitsForSelectors}
          onClose={() => setShowCreateCC(false)}
          onCreated={() => { loadCC(); }}
        />
      )}
      {showCreateRl31 && (
        <CreateRl31Modal
          masterId={masterId}
          properties={propertiesForSelectors}
          onClose={() => setShowCreateRl31(false)}
          onCreated={() => { load(); }}
        />
      )}
      {showCreateRI && (
        <CreateRentIncreaseModal
          masterId={masterId}
          units={unitsForSelectors}
          onClose={() => setShowCreateRI(false)}
          onCreated={() => { load(); }}
        />
      )}

      {/* Batch #14.214 — Modals édition admin */}
      {editingCC && (
        <CreateCreditCheckModal
          masterId={masterId}
          units={unitsForSelectors}
          existing={editingCC}
          onClose={() => setEditingCC(null)}
          onCreated={() => { loadCC(); }}
        />
      )}
      {editingRl31 && (
        <CreateRl31Modal
          masterId={masterId}
          properties={propertiesForSelectors}
          existing={editingRl31}
          onClose={() => setEditingRl31(null)}
          onCreated={() => { load(); }}
        />
      )}
      {editingRI && (
        <CreateRentIncreaseModal
          masterId={masterId}
          units={unitsForSelectors}
          existing={editingRI}
          onClose={() => setEditingRI(null)}
          onCreated={() => { load(); }}
        />
      )}
    </section>
  );
}

// ----------------- Modal détail enquête de crédit -----------------
function CreditCheckDetailModal({ cc, onClose, onReload }) {
  const [notes, setNotes] = useState(cc.admin_notes || "");
  const [status, setStatus] = useState(cc.status || "pending");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/admin/immeuble/credit-checks/${cc.id}/status`, { status, admin_notes: notes }, { headers: authH() });
      toast.success("Enquête mise à jour");
      onReload?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="zone2-cc-detail-modal">
        <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-base font-bold inline-flex items-center gap-2">
            <Shield className="w-5 h-5 text-emerald-600" /> Enquête de crédit
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Candidat</div>
            <div className="font-bold text-slate-800">{cc.candidate_first_name} {cc.candidate_last_name}</div>
            <div className="text-xs text-slate-600 mt-1">
              {cc.candidate_email && <div>📧 {cc.candidate_email}</div>}
              {cc.candidate_phone && <div>📞 {cc.candidate_phone}</div>}
              {cc.candidate_dob && <div>🎂 Date de naissance : {cc.candidate_dob}</div>}
              {cc.candidate_sin && <div className="text-rose-500 font-mono text-[10px]">NAS : {cc.candidate_sin}</div>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Logement</div>
              <div className="font-semibold">{cc.unit_label || cc.unit_id?.slice(0, 8)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date de la demande</div>
              <div className="font-semibold">{(cc.created_at || "").slice(0, 10)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Paiement</div>
              <div className="font-semibold">
                <PaymentBadge status={cc.payment_status} amount={cc.payment_amount_cad} />
                {!cc.payment_status && <span className="text-slate-400">—</span>}
              </div>
            </div>
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Référence</div>
              <div className="font-mono text-[10px]">{cc.id?.slice(0, 8)}…</div>
            </div>
          </div>

          {(cc.previous_address || cc.employer_name) && (
            <div className="bg-slate-50 rounded-lg p-2.5 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Contexte</div>
              {cc.previous_address && <div>🏠 Adresse précédente : <b>{cc.previous_address}</b></div>}
              {cc.employer_name && <div>💼 Employeur : <b>{cc.employer_name}</b> {cc.employer_income ? `· ${cc.employer_income} $/an` : ""}</div>}
            </div>
          )}

          {cc.notes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs">
              <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1">Notes du propriétaire</div>
              <div className="text-amber-900 whitespace-pre-wrap">{cc.notes}</div>
            </div>
          )}

          <div className="pt-3 border-t space-y-2">
            <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Décision admin</h4>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="cc-input" data-testid="zone2-cc-modal-status">
              <option value="pending">En attente</option>
              <option value="in_progress">En cours d&apos;enquête</option>
              <option value="accepted">Accepté</option>
              <option value="rejected">Refusé</option>
              <option value="completed">Complété</option>
            </select>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes internes (visibles côté client comme message de l'équipe)"
              className="cc-input w-full"
              data-testid="zone2-cc-modal-notes"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t bg-slate-50 flex items-center justify-end gap-2 sticky bottom-0">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border">Annuler</button>
          <button
            onClick={save}
            disabled={busy}
            className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-1.5"
            data-testid="zone2-cc-modal-save"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
